"use client"

/**
 * AnswerMonitor — the homepage hero's "answer intelligence" visual.
 * Faithful port of the approved reference (Velova Hero Visual v2), same
 * choreography and structure, adapted to this codebase's stack:
 *  - the reference is a hand-tuned, five-phase setTimeout timeline that
 *    writes styles directly to DOM nodes (not React state) — ported here
 *    the same way, via refs queried once on mount, so the exact easing/
 *    stagger/timing values carry over unchanged.
 *  - real competitor marks (Salesforce, HubSpot, Zoho, monday.com) per the
 *    approved reference — this is the one hero visual on the site allowed
 *    to show real, named competitors.
 *  - engine marks reuse this codebase's existing exact logo paths
 *    (components/landing/old/engine-logos.tsx) instead of the reference's
 *    static SVG files.
 *  - same responsive engine (single ResizeObserver → CSS custom properties
 *    for desktop/tablet/mobile), prefers-reduced-motion (jumps straight to
 *    the finished state, no loop), and IntersectionObserver play/pause.
 */

import { useEffect, useRef, useState } from "react"
import { EngineLogo } from "./old/engine-logos"
import { SalesforceLogo, HubSpotLogo, ZohoLogo, MondayLogo } from "./brand-logos"

const ACCENT = "var(--velova-blue-deep)"

const ENGINES = [
  { id: "chatgpt", label: "ChatGPT" },
  { id: "gemini", label: "Gemini" },
  { id: "perplex", label: "Perplexity" },
  { id: "claude", label: "Claude" },
] as const

/* Brand colour per engine mark. The marks are simple-icons monochrome paths
   (EngineLogo fills with currentColor), so we tint each one to its brand
   instead of a flat grey. */
const ENGINE_BRAND: Record<string, string> = {
  chatgpt: "#10A37F", // OpenAI green
  gemini: "#4285F4",  // Google / Gemini blue
  perplex: "#20B8CD", // Perplexity teal
  claude: "#D97757",  // Anthropic coral
}

const RANKED = [
  { num: "01", Logo: SalesforceLogo, name: "Salesforce", tag: "○ Leading", target: false, comp: true, barPct: 100, barW: "150px", cites: ["1", "2"] },
  { num: "02", Logo: HubSpotLogo, name: "HubSpot", tag: null, target: false, comp: false, barPct: 71, barW: "150px", cites: ["3"] },
  { num: "03", Logo: ZohoLogo, name: "Zoho CRM", tag: null, target: false, comp: false, barPct: 54, barW: "150px", cites: ["1"] },
  { num: "04", Logo: MondayLogo, name: "monday.com", tag: "◆ Tracked", target: true, comp: false, barPct: 37, barW: "150px", cites: ["4"] },
] as const

/** Velova's VA monogram (public/models/velova-mark.svg, the approved brand
 * kit symbol) — used in the recommendation card's icon badge in place of a
 * generic glyph. */
function VelovaMark({ size = 16, color = ACCENT }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size * (470 / 700)} viewBox="0 0 700 470" aria-hidden focusable="false">
      <path
        d="M 103.701 108.633 C 102.217 110.117, 103.209 112.516, 109.371 122.341 C 112.875 127.929, 119.463 138.575, 124.011 146 C 128.559 153.425, 137.241 167.600, 143.304 177.500 C 149.368 187.400, 161.164 206.750, 169.518 220.500 C 177.873 234.250, 194.967 262.375, 207.506 283 C 220.045 303.625, 234.208 327.025, 238.978 335 C 251.186 355.407, 252.010 356.559, 256.441 359.433 C 264.186 364.455, 277.838 362.249, 282.886 355.160 C 284.016 353.574, 290.494 342.651, 297.283 330.888 C 319.390 292.580, 325.121 282.714, 333.624 268.329 C 341.537 254.941, 343.485 250, 340.850 250 C 340.217 250, 336.097 250.730, 331.694 251.622 C 317.697 254.458, 304.028 250.365, 293.382 240.151 C 287.987 234.975, 271.392 208.887, 240.167 156.500 C 213.867 112.375, 214.950 114.036, 210.574 111.118 L 206.648 108.500 155.525 108.216 C 127.407 108.060, 104.086 108.247, 103.701 108.633 M 424.372 110.440 C 420.121 112.290, 417.103 116.322, 408.585 131.533 C 404.591 138.665, 391.801 160.672, 380.162 180.439 C 368.523 200.205, 359 217.417, 359 218.689 C 359 221.423, 360.081 221.513, 368.500 219.480 C 380.281 216.635, 391.149 218.673, 401.759 225.715 C 409.828 231.071, 415.310 238.876, 439.542 279.500 C 445.447 289.400, 453.762 303.125, 458.021 310 C 462.279 316.875, 469.715 329.250, 474.546 337.500 C 484.467 354.445, 489.502 360.250, 495.731 361.928 C 498.650 362.714, 514.634 362.973, 548.214 362.779 L 596.500 362.500 596.822 360.233 C 597.031 358.760, 594.372 353.510, 589.225 345.233 C 571.194 316.241, 559.171 296.712, 551.219 283.500 C 546.584 275.800, 539.743 264.550, 536.017 258.500 C 529.410 247.775, 519.063 230.769, 484.297 173.500 C 474.949 158.100, 463.446 138.975, 458.737 131 C 449.901 116.037, 446.525 111.892, 441.685 110.064 C 437.787 108.591, 428.138 108.801, 424.372 110.440"
        fill={color}
        fillRule="evenodd"
      />
    </svg>
  )
}

function set(el: HTMLElement | null | undefined, props: Partial<CSSStyleDeclaration>) {
  if (el) Object.assign(el.style, props)
}
function setAll(els: (HTMLElement | null | undefined)[], props: Partial<CSSStyleDeclaration>) {
  els.forEach((el) => set(el, props))
}

export function AnswerMonitor({ speed = 1 }: { speed?: number }) {
  const hostRef = useRef<HTMLDivElement>(null)
  const winRef = useRef<HTMLElement>(null)
  const [activeEngine, setActiveEngine] = useState(0)
  const [ctaHover, setCtaHover] = useState(false)

  useEffect(() => {
    const root = hostRef.current
    if (!root) return

    const q = (sel: string) => Array.from(root.querySelectorAll<HTMLElement>(sel))
    const els = {
      pSteps: q('[data-vhv="pstep"]'),
      status: root.querySelector<HTMLElement>('[data-vhv="status"]'),
      prompt: root.querySelector<HTMLElement>('[data-vhv="prompt"]'),
      thinking: root.querySelector<HTMLElement>('[data-vhv="thinking"]'),
      scan: root.querySelector<HTMLElement>('[data-vhv="scan"]'),
      lines: q('[data-vhv="line"]'),
      bars: q('[data-vhv="bar"]'),
      cites: q('[data-vhv="cite"]'),
      tags: q('[data-vhv="tag"]'),
      hlTarget: root.querySelector<HTMLElement>('[data-vhv="hl-target"]'),
      hlComp: root.querySelector<HTMLElement>('[data-vhv="hl-comp"]'),
      kpis: q('[data-vhv="kpi"]'),
      sweep: root.querySelector<HTMLElement>('[data-vhv="sweep"]'),
      sent: root.querySelector<HTMLElement>('[data-vhv="sent"]'),
      diagnosis: root.querySelector<HTMLElement>('[data-vhv="diagnosis"]'),
      rec: root.querySelector<HTMLElement>('[data-vhv="rec"]'),
      recEdge: root.querySelector<HTMLElement>('[data-vhv="rec-edge"]'),
    }
    const kpiParts = (sel: string) => els.kpis.map((k) => k.querySelector<HTMLElement>(sel))
    const kpiSegs = () => els.kpis.flatMap((k) => Array.from(k.querySelectorAll<HTMLElement>('[data-vhv="seg"]')))

    // ---- responsive: one ResizeObserver drives layout custom props -------
    let mode = ""
    const layout = (w: number) => {
      const next = w >= 620 ? "desktop" : w >= 470 ? "tablet" : "mobile"
      if (next === mode) return
      mode = next
      const s = root.style
      // The four analysis tiles read as one row (matching the approved
      // reference) at any width that isn't a true narrow mobile stack —
      // this is a wider floor than the other tablet-tier vars below.
      s.setProperty("--vhv-tiles", w >= 470 ? "repeat(4, 1fr)" : "repeat(2, 1fr)")
      if (w >= 620) {
        s.setProperty("--vhv-name-w", "92px")
        s.setProperty("--vhv-bar", "block")
        s.setProperty("--vhv-bar-w", "150px")
        s.setProperty("--vhv-prompt-fs", "17px")
        s.setProperty("--vhv-rec-fs", "14.5px")
        s.setProperty("--vhv-pad", "20px")
        s.setProperty("--vhv-engine-name", "inline")
        s.setProperty("--vhv-cta", "flex")
      } else if (w >= 470) {
        s.setProperty("--vhv-name-w", "80px")
        s.setProperty("--vhv-bar", "block")
        s.setProperty("--vhv-bar-w", "72px")
        s.setProperty("--vhv-prompt-fs", "15px")
        s.setProperty("--vhv-rec-fs", "12.5px")
        s.setProperty("--vhv-pad", "15px")
        s.setProperty("--vhv-engine-name", "inline")
        s.setProperty("--vhv-cta", "flex")
      } else {
        s.setProperty("--vhv-name-w", "auto")
        s.setProperty("--vhv-bar", "none")
        s.setProperty("--vhv-bar-w", "0px")
        s.setProperty("--vhv-prompt-fs", "15px")
        s.setProperty("--vhv-rec-fs", "13px")
        s.setProperty("--vhv-pad", "14px")
        s.setProperty("--vhv-engine-name", "none")
        s.setProperty("--vhv-cta", "none")
      }
    }
    layout(winRef.current ? winRef.current.getBoundingClientRect().width : 720)
    let rafId = 0
    let ro: ResizeObserver | undefined
    if (window.ResizeObserver && winRef.current) {
      ro = new ResizeObserver((entries) => {
        const w = entries[0].contentRect.width
        if (rafId) cancelAnimationFrame(rafId)
        rafId = requestAnimationFrame(() => layout(w))
      })
      ro.observe(winRef.current)
    }

    // ---- timeline plumbing -------------------------------------------------
    const timers: ReturnType<typeof setTimeout>[] = []
    let loopTimer: ReturnType<typeof setTimeout> | null = null
    let playing = false
    const scale = () => 1 / (Number(speed) || 1)
    const at = (ms: number, fn: () => void) => timers.push(setTimeout(fn, ms * scale()))
    const clearTimers = () => {
      timers.forEach(clearTimeout)
      timers.length = 0
      if (loopTimer) clearTimeout(loopTimer)
    }
    const pause = () => {
      playing = false
      clearTimers()
    }

    const restoreTransitions = () => {
      setAll(els.lines, { transition: "opacity .55s ease, transform .55s cubic-bezier(.2,.7,.2,1), filter .55s ease" })
      setAll(els.bars, { transition: "transform .95s cubic-bezier(.2,.7,.2,1)" })
      setAll([...els.cites, ...els.tags], { transition: "opacity .5s ease" })
      setAll(kpiParts('[data-vhv="kpi-val"]'), { transition: "opacity .5s ease, transform .5s cubic-bezier(.2,.7,.2,1)" })
      setAll(kpiParts('[data-vhv="kpi-skel"]'), { transition: "opacity .3s ease" })
      setAll(kpiSegs(), { transition: "opacity .5s ease" })
      set(els.prompt, { transition: "opacity .7s ease, transform .7s cubic-bezier(.2,.7,.2,1)" })
      set(els.hlTarget, { transition: "opacity .6s ease" })
      set(els.hlComp, { transition: "opacity .6s ease" })
      set(els.sweep, { transition: "transform 1.9s cubic-bezier(.4,0,.2,1)" })
      set(els.sent, { transition: "opacity .5s ease" })
      set(els.diagnosis, { transition: "opacity .6s ease, transform .6s cubic-bezier(.2,.7,.2,1)" })
      set(els.rec, { transition: "opacity .7s ease, transform .7s cubic-bezier(.2,.7,.2,1)" })
      set(els.recEdge, { transition: "transform .9s cubic-bezier(.2,.7,.2,1)" })
    }

    const status = (text: string) => {
      if (els.status) els.status.textContent = text
    }
    const stepsFn = (n: number) => {
      els.pSteps.forEach((el, i) => set(el, { transition: "opacity .7s ease", opacity: i < n ? "1" : ".1" }))
    }

    let engineIndex = 0
    const activateEngine = () => {
      setActiveEngine(engineIndex % ENGINES.length)
      engineIndex = (engineIndex + 1) % ENGINES.length
    }

    const runCycle = () => {
      // ---- reset, transitions off so nothing animates backwards ---------
      setAll(els.lines, { transition: "none", opacity: "0", transform: "translateY(8px)", filter: "blur(2px)" })
      setAll(els.bars, { transition: "none", transform: "scaleX(0)" })
      setAll([...els.cites, ...els.tags], { transition: "none", opacity: "0" })
      setAll(kpiParts('[data-vhv="kpi-val"]'), { transition: "none", opacity: "0", transform: "translateY(5px)" })
      setAll(kpiParts('[data-vhv="kpi-skel"]'), { transition: "none", opacity: "1" })
      setAll(kpiSegs(), { transition: "none", opacity: "0" })
      set(els.prompt, { transition: "none", opacity: "0", transform: "translateY(6px)" })
      set(els.hlTarget, { transition: "none", opacity: "0" })
      set(els.hlComp, { transition: "none", opacity: "0" })
      set(els.sweep, { transition: "none", transform: "scaleX(0)" })
      set(els.sent, { transition: "none", opacity: "0" })
      set(els.diagnosis, { transition: "none", opacity: "0", transform: "translateY(6px)" })
      set(els.rec, { transition: "none", opacity: "0", transform: "translateY(10px)" })
      set(els.recEdge, { transition: "none", transform: "scaleX(0)" })
      setAll(els.pSteps, { transition: "none", opacity: ".1" })
      set(els.scan, { animation: "none", opacity: "0" })

      requestAnimationFrame(() => requestAnimationFrame(restoreTransitions))

      // ---- PHASE 1 · question preparation --------------------------------
      at(100, () => {
        activateEngine()
        status("Engine selected")
        stepsFn(1)
      })
      at(300, () => set(els.prompt, { opacity: "1", transform: "none" }))
      at(700, () => {
        status("Collecting answer")
        set(els.thinking, { opacity: "1" })
        set(els.scan, { animation: "vhvScan 2.2s cubic-bezier(.4,0,.6,1) 1" })
      })

      // ---- PHASE 2 · answer reveals block by block, then bars fill ------
      els.lines.forEach((el, i) => at(1300 + i * 230, () => set(el, { opacity: "1", transform: "none", filter: "none" })))
      els.bars.forEach((el, i) => at(1700 + i * 230, () => set(el, { transform: "scaleX(1)" })))
      at(2600, () => {
        set(els.thinking, { opacity: "0" })
        set(els.scan, { animation: "none", opacity: "0" })
      })
      at(2750, () => {
        status("Parsing mentions")
        stepsFn(2)
        set(els.hlComp, { opacity: "1" })
        set(els.tags[0], { opacity: "1" })
      })
      at(3050, () => {
        set(els.hlTarget, { opacity: "1" })
        set(els.tags[1], { opacity: "1" })
      })
      els.cites.forEach((el, i) => at(3300 + i * 90, () => set(el, { opacity: "1" })))

      // ---- PHASE 3 · Velova analysis, one tile at a time -----------------
      at(3900, () => {
        status("Analysing answer")
        stepsFn(3)
        set(els.sweep, { transform: "scaleX(1)" })
      })
      els.kpis.forEach((kpi, i) =>
        at(4050 + i * 380, () => {
          set(kpi.querySelector<HTMLElement>('[data-vhv="kpi-skel"]'), { opacity: "0" })
          set(kpi.querySelector<HTMLElement>('[data-vhv="kpi-val"]'), { opacity: "1", transform: "none" })
          Array.from(kpi.querySelectorAll<HTMLElement>('[data-vhv="seg"]')).forEach((seg, j) =>
            at(110 + j * 60, () => set(seg, { opacity: "1" })),
          )
        }),
      )
      at(5650, () => set(els.sent, { opacity: "1" }))

      // ---- PHASE 4 · diagnosis, then the recommendation ------------------
      at(6150, () => {
        status("Diagnosing")
        set(els.diagnosis, { opacity: "1", transform: "none" })
      })
      at(6850, () => {
        status("Action ready")
        stepsFn(4)
        set(els.rec, { opacity: "1", transform: "none" })
        set(els.recEdge, { transform: "scaleX(1)" })
      })

      // ---- PHASE 5 · hold, then fade out and restart ---------------------
      at(11450, () => {
        const fade: Partial<CSSStyleDeclaration> = { transition: "opacity .6s ease", opacity: "0" }
        setAll([...els.lines, ...els.cites, ...els.tags], fade)
        ;[els.prompt, els.hlTarget, els.hlComp, els.sent, els.diagnosis, els.rec].forEach((el) => set(el, fade))
        setAll(els.pSteps, { transition: "opacity .6s ease", opacity: ".1" })
        setAll(kpiParts('[data-vhv="kpi-val"]'), fade)
        setAll(kpiSegs(), fade)
      })
    }

    const loop = () => {
      if (!playing) return
      clearTimers()
      runCycle()
      loopTimer = setTimeout(loop, 12400 * scale())
    }
    const play = () => {
      if (!playing) {
        playing = true
        loop()
      }
    }

    const complete = () => {
      pause()
      const on: Partial<CSSStyleDeclaration> = { transition: "none", opacity: "1" }
      setAll(els.lines, { transition: "none", opacity: "1", transform: "none", filter: "none" })
      setAll(els.bars, { transition: "none", transform: "scaleX(1)" })
      setAll([...els.cites, ...els.tags], on)
      setAll(kpiParts('[data-vhv="kpi-val"]'), { transition: "none", opacity: "1", transform: "none" })
      setAll(kpiParts('[data-vhv="kpi-skel"]'), { transition: "none", opacity: "0" })
      setAll(kpiSegs(), on)
      set(els.prompt, { transition: "none", opacity: "1", transform: "none" })
      set(els.hlTarget, on)
      set(els.hlComp, on)
      set(els.sweep, { transition: "none", transform: "scaleX(1)" })
      set(els.sent, on)
      set(els.thinking, { opacity: "0" })
      set(els.scan, { animation: "none", opacity: "0" })
      set(els.diagnosis, { transition: "none", opacity: "1", transform: "none" })
      set(els.rec, { transition: "none", opacity: "1", transform: "none" })
      set(els.recEdge, { transition: "none", transform: "scaleX(1)" })
      setAll(els.pSteps, { transition: "none", opacity: "1" })
      status("Action ready")
    }

    // reduced motion: the template's default markup IS the completed state
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      complete()
      return () => {
        if (ro) ro.disconnect()
        if (rafId) cancelAnimationFrame(rafId)
      }
    }

    let io: IntersectionObserver | undefined
    if (window.IntersectionObserver) {
      io = new IntersectionObserver((entries) => (entries[0].isIntersecting ? play() : pause()), { threshold: 0.15 })
      io.observe(root)
    } else {
      play()
    }

    return () => {
      pause()
      if (rafId) cancelAnimationFrame(rafId)
      if (ro) ro.disconnect()
      if (io) io.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speed])

  return (
    <div
      ref={hostRef}
      style={{
        position: "relative",
        display: "grid",
        placeItems: "center",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          width: 940,
          maxWidth: "132%",
          height: 720,
          pointerEvents: "none",
          background:
            "radial-gradient(50% 44% at 44% 40%, rgba(56,196,184,.17), transparent 72%), radial-gradient(46% 42% at 74% 80%, rgba(178,224,240,.14), transparent 72%)",
          filter: "blur(30px)",
        }}
      />

      <section
        ref={winRef}
        aria-label="Illustrative Velova interface: an AI answer, Velova's analysis of it, and the recommended next step"
        style={{
          position: "relative",
          width: "min(720px, 100%)",
          boxSizing: "border-box",
          borderRadius: 20,
          border: "1px solid rgba(160,196,224,.13)",
          background: "linear-gradient(178deg, #070A10 0%, #05070C 46%, #04060A 100%)",
          boxShadow: "0 48px 110px -34px rgba(0,0,0,.95), 0 2px 6px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,255,255,.05)",
          overflow: "hidden",
          color: "#E8EEF4",
        }}
      >
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background: "radial-gradient(58% 34% at 12% 0%, rgba(178,224,240,.07), transparent 70%)",
          }}
        />

        {/* phase rail */}
        <div aria-hidden style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, display: "flex", gap: 2, background: "rgba(255,255,255,.04)" }}>
          {[0, 1, 2].map((i) => (
            <span key={i} data-vhv="pstep" style={{ flex: 1, background: `linear-gradient(90deg, rgba(178,224,240,.4), ${ACCENT})`, opacity: 1, transition: "opacity .7s ease" }} />
          ))}
          <span data-vhv="pstep" style={{ flex: 1, background: "linear-gradient(90deg, rgba(178,224,240,.4), #B2E0F0)", opacity: 1, transition: "opacity .7s ease" }} />
        </div>

        {/* engine selector */}
        <header style={{ position: "relative", display: "flex", alignItems: "center", gap: 14, padding: "12px var(--vhv-pad, 20px)", borderBottom: "1px solid rgba(255,255,255,.06)" }}>
          <div role="group" aria-label="AI engines analysed" style={{ display: "flex", alignItems: "center", gap: 5, flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}>
            {ENGINES.map((eng, i) => {
              const on = i === activeEngine
              return (
                <span
                  key={eng.id}
                  style={{
                    flex: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 9px 4px 5px",
                    borderRadius: 8,
                    border: `1px solid ${on ? "rgba(178,224,240,.34)" : "rgba(255,255,255,.08)"}`,
                    background: on ? "rgba(178,224,240,.09)" : "transparent",
                    fontSize: 11,
                    fontWeight: 500,
                    color: on ? "#E8EEF4" : "rgba(232,238,244,.6)",
                    transition: "border-color .5s ease, background .5s ease, color .5s ease",
                  }}
                >
                  <EngineLogo id={eng.id} className="w-[15px] h-[15px] shrink-0" style={{ color: ENGINE_BRAND[eng.id], opacity: on ? 1 : 0.72, transition: "opacity .5s ease" }} />
                  <span style={{ display: "var(--vhv-engine-name, inline)" }}>{eng.label}</span>
                </span>
              )
            })}
          </div>
          <span style={{ flex: "none", display: "flex", alignItems: "center", gap: 6 }}>
            <span aria-hidden style={{ width: 5, height: 5, borderRadius: "50%", background: ACCENT, boxShadow: "0 0 8px rgba(178,224,240,.7)" }} />
            <span data-vhv="status" aria-hidden className="velova-mono-label" style={{ letterSpacing: ".12em", color: "rgba(232,238,244,.62)", whiteSpace: "nowrap" }}>
              Action ready
            </span>
          </span>
        </header>

        {/* question + answer */}
        <div style={{ position: "relative", padding: "13px var(--vhv-pad, 20px) 0" }}>
          <div style={{ position: "relative", padding: "12px 13px 13px", border: "1px solid rgba(255,255,255,.06)", borderRadius: 12, background: "linear-gradient(180deg, rgba(255,255,255,.028), rgba(255,255,255,.006))", overflow: "hidden" }}>
            <div
              data-vhv="scan"
              aria-hidden
              style={{ position: "absolute", left: 0, right: 0, top: 0, height: 84, opacity: 0, pointerEvents: "none", background: "linear-gradient(180deg, transparent, rgba(178,224,240,.06) 55%, rgba(178,224,240,.15) 84%, transparent)" }}
            />

            <p data-vhv="prompt" style={{ margin: 0, fontSize: "var(--vhv-prompt-fs, 17px)", lineHeight: 1.35, fontWeight: 500, letterSpacing: "-.012em", color: "#EFF4F9", opacity: 1, transform: "none", transition: "opacity .7s ease, transform .7s cubic-bezier(.2,.7,.2,1)" }}>
              &ldquo;Which CRM is best for a fast-growing B2B company?&rdquo;
            </p>

            <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "13px 0 11px", paddingTop: 12, borderTop: "1px solid rgba(255,255,255,.06)" }}>
              <EngineLogo id={ENGINES[activeEngine].id} className="w-[13px] h-[13px] shrink-0" style={{ color: ENGINE_BRAND[ENGINES[activeEngine].id] }} />
              <span data-vhv="answer-engine" className="velova-mono-label" style={{ letterSpacing: ".12em", color: "rgba(232,238,244,.62)" }}>
                {ENGINES[activeEngine].label}
              </span>
              <span data-vhv="thinking" aria-hidden style={{ display: "flex", gap: 3, opacity: 0, transition: "opacity .4s ease" }}>
                {[0, 0.18, 0.36].map((delay, i) => (
                  <span key={i} style={{ width: 3, height: 3, borderRadius: "50%", background: ACCENT, animation: `vhvThink 1.25s ease-in-out ${delay}s infinite` }} />
                ))}
              </span>
              <span aria-hidden style={{ flex: 1, height: 1, background: "rgba(255,255,255,.05)" }} />
            </div>

            <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 5 }}>
              {RANKED.map((row, i) => (
                <li
                  key={row.name}
                  data-vhv="line"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "5px 7px 5px 5px",
                    borderRadius: 8,
                    background: row.target ? "rgba(178,224,240,.055)" : "rgba(255,255,255,.014)",
                    boxShadow: row.target ? "inset 2px 0 0 rgba(178,224,240,.6)" : undefined,
                    opacity: 1,
                    transform: "none",
                    filter: "none",
                    transition: "opacity .55s ease, transform .55s cubic-bezier(.2,.7,.2,1), filter .55s ease",
                  }}
                >
                  <span aria-hidden className="velova-mono-label" style={{ flex: "none", letterSpacing: 0, color: "rgba(232,238,244,.58)" }}>
                    {row.num}
                  </span>
                  <span
                    aria-hidden
                    style={{
                      flex: "none",
                      display: "grid",
                      placeItems: "center",
                      width: 20,
                      height: 20,
                      borderRadius: 6,
                      border: `1px solid ${row.target ? "rgba(178,224,240,.42)" : "rgba(255,255,255,.1)"}`,
                      background: row.target ? "rgba(178,224,240,.14)" : "rgba(255,255,255,.07)",
                    }}
                  >
                    <row.Logo className="w-[13px] h-[13px]" />
                  </span>
                  <span style={{ flex: "none", width: "var(--vhv-name-w, 92px)", fontSize: 12, fontWeight: 600, color: "#F2F6FA" }}>
                    <span style={{ position: "relative", display: "inline-block" }}>
                      {row.comp && <span data-vhv="hl-comp" style={{ position: "absolute", inset: "-3px -6px", borderRadius: 5, border: "1px solid rgba(232,238,244,.2)", background: "rgba(232,238,244,.07)", opacity: 0, transition: "opacity .6s ease" }} />}
                      {row.target && <span data-vhv="hl-target" style={{ position: "absolute", inset: "-3px -6px", borderRadius: 5, border: "1px solid rgba(178,224,240,.45)", background: "rgba(178,224,240,.14)", boxShadow: "0 0 16px -4px rgba(178,224,240,.5)", opacity: 0, transition: "opacity .6s ease" }} />}
                      <span style={{ position: "relative" }}>{row.name}</span>
                    </span>
                  </span>
                  {row.tag && (
                    <span
                      data-vhv="tag"
                      className="velova-mono-label"
                      style={{
                        flex: "none",
                        padding: "2px 5px",
                        borderRadius: 4,
                        border: `1px solid ${row.target ? "rgba(178,224,240,.36)" : "rgba(232,238,244,.17)"}`,
                        background: row.target ? "rgba(178,224,240,.12)" : "rgba(232,238,244,.04)",
                        letterSpacing: ".1em",
                        whiteSpace: "nowrap",
                        color: row.target ? "#BEE9F5" : "rgba(232,238,244,.55)",
                        opacity: 0,
                        transition: "opacity .5s ease",
                      }}
                    >
                      {row.tag}
                    </span>
                  )}
                  <span aria-hidden style={{ flex: "1 1 auto", minWidth: 6 }} />
                  <span aria-hidden style={{ flex: "none", display: "var(--vhv-bar, block)", width: "var(--vhv-bar-w, 150px)", height: 4, borderRadius: 2, background: "rgba(255,255,255,.07)", overflow: "hidden" }}>
                    <span
                      data-vhv="bar"
                      style={{
                        display: "block",
                        width: `${row.barPct}%`,
                        height: "100%",
                        borderRadius: 2,
                        background: row.target ? `linear-gradient(90deg, rgba(178,224,240,.5), ${ACCENT})` : "linear-gradient(90deg, rgba(170,206,228,.3), rgba(170,206,228,.72))",
                        boxShadow: row.target ? "0 0 10px rgba(178,224,240,.45)" : undefined,
                        transform: "scaleX(0)",
                        transformOrigin: "left",
                        transition: "transform .95s cubic-bezier(.2,.7,.2,1)",
                      }}
                    />
                  </span>
                  <span style={{ flex: "none", display: "flex", gap: 3 }}>
                    {row.cites.map((c, ci) => (
                      <span key={ci} data-vhv="cite" className="velova-mono-label" style={{ padding: "1px 4px", borderRadius: 4, background: "rgba(111,168,245,.14)", letterSpacing: 0, color: "#B2E0F0", opacity: 0, transition: "opacity .5s ease" }}>
                        {c}
                      </span>
                    ))}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* analysis */}
        <div style={{ padding: "13px var(--vhv-pad, 20px) 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span className="velova-mono-label" style={{ letterSpacing: ".15em", color: "rgba(232,238,244,.5)" }}>Analysis</span>
            <span aria-hidden style={{ position: "relative", flex: 1, height: 1, background: "rgba(255,255,255,.06)", overflow: "hidden" }}>
              <span data-vhv="sweep" style={{ position: "absolute", inset: 0, background: `linear-gradient(90deg, ${ACCENT}, rgba(178,224,240,.1))`, transform: "scaleX(0)", transformOrigin: "left", transition: "transform 1.9s cubic-bezier(.4,0,.2,1)" }} />
            </span>
            <span data-vhv="sent" className="velova-mono-label" style={{ display: "flex", alignItems: "center", gap: 5, letterSpacing: ".12em", color: "rgba(232,238,244,.58)", opacity: 0, transition: "opacity .5s ease" }}>
              Sentiment<span aria-hidden style={{ color: "rgba(140,220,205,.9)" }}>▲</span><span style={{ color: "rgba(232,238,244,.75)" }}>Positive</span>
            </span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "var(--vhv-tiles, repeat(4, 1fr))", gap: 8 }}>
            {/* Position */}
            <div data-vhv="kpi" style={{ padding: "10px 11px 11px", borderRadius: 10, border: "1px solid rgba(255,255,255,.06)", background: "linear-gradient(180deg, rgba(255,255,255,.028), rgba(255,255,255,.006))", minWidth: 0 }}>
              <span className="velova-mono-label" style={{ display: "block", letterSpacing: ".13em", color: "rgba(232,238,244,.6)" }}>Position</span>
              <span style={{ position: "relative", display: "block", minHeight: 25, marginTop: 7 }}>
                <span data-vhv="kpi-skel" aria-hidden style={{ position: "absolute", left: 0, top: 4, fontSize: 13, color: "rgba(232,238,244,.15)", opacity: 1, transition: "opacity .3s ease" }}>——</span>
                <span data-vhv="kpi-val" style={{ display: "flex", alignItems: "baseline", gap: 5, whiteSpace: "nowrap", opacity: 0, transform: "translateY(5px)", transition: "opacity .5s ease, transform .5s cubic-bezier(.2,.7,.2,1)" }}>
                  <strong style={{ fontSize: 20, fontWeight: 600, letterSpacing: "-.02em", color: "#F2F6FA" }}>4th</strong>
                  <span style={{ fontSize: 10.5, color: "rgba(232,238,244,.58)" }}>of 4</span>
                </span>
              </span>
              <span aria-hidden style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 22, marginTop: 9 }}>
                {[
                  { h: "100%", bg: "rgba(170,206,228,.3)" },
                  { h: "68%", bg: "rgba(170,206,228,.24)" },
                  { h: "44%", bg: "rgba(170,206,228,.2)" },
                  { h: "26%", bg: ACCENT, glow: true },
                ].map((seg, i) => (
                  <span key={i} data-vhv="seg" style={{ borderRadius: 2, opacity: 0, transition: "opacity .5s ease", flex: 1, height: seg.h, background: seg.bg, boxShadow: seg.glow ? "0 0 12px -2px rgba(178,224,240,.6)" : undefined }} />
                ))}
              </span>
            </div>

            {/* Answer coverage */}
            <div data-vhv="kpi" style={{ padding: "10px 11px 11px", borderRadius: 10, border: "1px solid rgba(255,255,255,.06)", background: "linear-gradient(180deg, rgba(255,255,255,.028), rgba(255,255,255,.006))", minWidth: 0 }}>
              <span className="velova-mono-label" style={{ display: "block", letterSpacing: ".13em", color: "rgba(232,238,244,.6)" }}>Answer coverage</span>
              <span style={{ position: "relative", display: "block", minHeight: 25, marginTop: 7 }}>
                <span data-vhv="kpi-skel" aria-hidden style={{ position: "absolute", left: 0, top: 4, fontSize: 13, color: "rgba(232,238,244,.15)", opacity: 1, transition: "opacity .3s ease" }}>——</span>
                <span data-vhv="kpi-val" style={{ display: "flex", alignItems: "baseline", gap: 5, whiteSpace: "nowrap", opacity: 0, transform: "translateY(5px)", transition: "opacity .5s ease, transform .5s cubic-bezier(.2,.7,.2,1)" }}>
                  <strong style={{ fontSize: 20, fontWeight: 600, letterSpacing: "-.02em", color: "#F2F6FA" }}>3</strong>
                  <span style={{ fontSize: 10.5, color: "rgba(232,238,244,.58)" }}>of 8 questions</span>
                </span>
              </span>
              <span aria-hidden style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 22, marginTop: 9 }}>
                {[1, 1, 1, 0, 0, 0, 0, 0].map((on, i) => (
                  <span key={i} data-vhv="seg" style={{ borderRadius: 2, opacity: 0, transition: "opacity .5s ease", flex: 1, height: "62%", background: on ? ACCENT : "rgba(255,255,255,.09)" }} />
                ))}
              </span>
            </div>

            {/* Top rival */}
            <div data-vhv="kpi" style={{ padding: "10px 11px 11px", borderRadius: 10, border: "1px solid rgba(255,255,255,.06)", background: "linear-gradient(180deg, rgba(255,255,255,.028), rgba(255,255,255,.006))", minWidth: 0 }}>
              <span className="velova-mono-label" style={{ display: "block", letterSpacing: ".13em", color: "rgba(232,238,244,.6)" }}>Top rival</span>
              <span style={{ position: "relative", display: "block", minHeight: 25, marginTop: 7 }}>
                <span data-vhv="kpi-skel" aria-hidden style={{ position: "absolute", left: 0, top: 4, fontSize: 13, color: "rgba(232,238,244,.15)", opacity: 1, transition: "opacity .3s ease" }}>——</span>
                <span data-vhv="kpi-val" style={{ display: "flex", alignItems: "baseline", gap: 5, whiteSpace: "nowrap", opacity: 0, transform: "translateY(5px)", transition: "opacity .5s ease, transform .5s cubic-bezier(.2,.7,.2,1)" }}>
                  <strong style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-.02em", color: "#F2F6FA" }}>Salesforce</strong>
                  <span style={{ fontSize: 10.5, color: "rgba(232,238,244,.58)" }}>1st</span>
                </span>
              </span>
              <span aria-hidden style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 6, height: 22, marginTop: 9 }}>
                <span data-vhv="seg" style={{ opacity: 0, transition: "opacity .5s ease", display: "flex", alignItems: "center", gap: 6 }}>
                  <SalesforceLogo className="w-[11px] h-[11px] shrink-0" />
                  <span style={{ flex: 1, height: 4, borderRadius: 2, background: "rgba(255,255,255,.07)" }}>
                    <span style={{ display: "block", width: "100%", height: "100%", borderRadius: 2, background: "rgba(170,206,228,.6)" }} />
                  </span>
                </span>
                <span data-vhv="seg" style={{ opacity: 0, transition: "opacity .5s ease", display: "flex", alignItems: "center", gap: 6 }}>
                  <MondayLogo className="w-[11px] h-[11px] shrink-0" />
                  <span style={{ flex: 1, height: 4, borderRadius: 2, background: "rgba(255,255,255,.07)" }}>
                    <span style={{ display: "block", width: "37%", height: "100%", borderRadius: 2, background: ACCENT }} />
                  </span>
                </span>
              </span>
            </div>

            {/* Source gap */}
            <div data-vhv="kpi" style={{ padding: "10px 11px 11px", borderRadius: 10, border: "1px solid rgba(255,255,255,.06)", background: "linear-gradient(180deg, rgba(255,255,255,.028), rgba(255,255,255,.006))", minWidth: 0 }}>
              <span className="velova-mono-label" style={{ display: "block", letterSpacing: ".13em", color: "rgba(232,238,244,.6)" }}>Source gap</span>
              <span style={{ position: "relative", display: "block", minHeight: 25, marginTop: 7 }}>
                <span data-vhv="kpi-skel" aria-hidden style={{ position: "absolute", left: 0, top: 4, fontSize: 13, color: "rgba(232,238,244,.15)", opacity: 1, transition: "opacity .3s ease" }}>——</span>
                <span data-vhv="kpi-val" style={{ display: "flex", alignItems: "baseline", gap: 5, whiteSpace: "nowrap", opacity: 0, transform: "translateY(5px)", transition: "opacity .5s ease, transform .5s cubic-bezier(.2,.7,.2,1)" }}>
                  <strong style={{ fontSize: 20, fontWeight: 600, letterSpacing: "-.02em", color: "#F2F6FA" }}>3</strong>
                  <span style={{ fontSize: 10.5, color: "rgba(232,238,244,.58)" }}>of 6 domains</span>
                </span>
              </span>
              <span aria-hidden style={{ display: "flex", alignItems: "center", gap: 4, height: 22, marginTop: 9 }}>
                {[true, true, true, false, false, false].map((filled, i) => (
                  <span
                    key={i}
                    data-vhv="seg"
                    style={{
                      borderRadius: 2,
                      opacity: 0,
                      transition: "opacity .5s ease",
                      width: 13,
                      height: 11,
                      background: filled ? "rgba(170,206,228,.42)" : undefined,
                      border: filled ? undefined : "1px dashed rgba(232,238,244,.34)",
                      boxSizing: "border-box",
                    }}
                  />
                ))}
              </span>
            </div>
          </div>
        </div>

        {/* diagnosis */}
        <div data-vhv="diagnosis" style={{ display: "flex", alignItems: "baseline", gap: 9, margin: "12px var(--vhv-pad, 20px) 0", opacity: 0, transform: "translateY(6px)", transition: "opacity .6s ease, transform .6s cubic-bezier(.2,.7,.2,1)" }}>
          <span aria-hidden style={{ flex: "none", marginTop: 5, width: 5, height: 5, borderRadius: 1, transform: "rotate(45deg)", background: ACCENT }} />
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: "rgba(232,238,244,.72)" }}>monday.com is cited for ease of use, not for enterprise CRM depth.</p>
        </div>

        {/* recommendation */}
        <div
          data-vhv="rec"
          style={{
            position: "relative",
            margin: "12px var(--vhv-pad, 20px) 15px",
            padding: "13px 14px 12px",
            borderRadius: 13,
            border: "1px solid rgba(178,224,240,.24)",
            background: "linear-gradient(135deg, rgba(178,224,240,.11), rgba(76,134,232,.055) 55%, rgba(178,224,240,.025))",
            boxShadow: "0 20px 44px -28px rgba(178,224,240,.55)",
            opacity: 0,
            transform: "translateY(10px)",
            transition: "opacity .7s ease, transform .7s cubic-bezier(.2,.7,.2,1)",
          }}
        >
          <span data-vhv="rec-edge" aria-hidden style={{ position: "absolute", top: -1, left: 16, right: 16, height: 1, background: `linear-gradient(90deg, transparent, ${ACCENT}, transparent)`, transform: "scaleX(0)", transformOrigin: "center", transition: "transform .9s cubic-bezier(.2,.7,.2,1)" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
            <span aria-hidden style={{ flex: "none", display: "grid", placeItems: "center", width: 34, height: 34, borderRadius: 10, border: "1px solid rgba(178,224,240,.42)", background: "rgba(178,224,240,.15)", boxShadow: "0 0 20px -7px rgba(178,224,240,.7)" }}>
              <VelovaMark size={16} />
            </span>
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>
              <span className="velova-mono-label" style={{ display: "block", letterSpacing: ".16em", color: "rgba(190,233,245,.9)" }}>Recommended next step</span>
              <p style={{ margin: "7px 0 0", fontSize: "var(--vhv-rec-fs, 14.5px)", lineHeight: 1.4, fontWeight: 500, letterSpacing: "-.01em", color: "#F2F7FA" }}>
                Strengthen enterprise proof on the product page and address three recurring citation gaps.
              </p>
            </div>
            <a
              href="#velova-recommendation"
              onMouseEnter={() => setCtaHover(true)}
              onMouseLeave={() => setCtaHover(false)}
              style={{
                flex: "none",
                display: "var(--vhv-cta, flex)",
                alignItems: "center",
                gap: 7,
                padding: "9px 13px",
                borderRadius: 9,
                border: `1px solid ${ctaHover ? "rgba(178,224,240,.75)" : "rgba(178,224,240,.5)"}`,
                background: ctaHover ? "linear-gradient(180deg, rgba(178,224,240,.34), rgba(178,224,240,.2))" : "linear-gradient(180deg, rgba(178,224,240,.22), rgba(178,224,240,.12))",
                fontSize: 11.5,
                fontWeight: 600,
                letterSpacing: "-.005em",
                color: "#DEF6FC",
                whiteSpace: "nowrap",
                transition: "background .3s ease, border-color .3s ease",
              }}
            >
              View recommendation<span aria-hidden>→</span>
            </a>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", marginTop: 11, paddingTop: 10, borderTop: "1px solid rgba(178,224,240,.15)" }}>
            <span className="velova-mono-label" style={{ display: "flex", alignItems: "center", gap: 5, letterSpacing: ".1em", color: "rgba(232,238,244,.62)" }}>
              <span aria-hidden style={{ color: "rgba(232,238,244,.8)" }}>▲</span>High priority
            </span>
            <span className="velova-mono-label" style={{ display: "flex", alignItems: "center", gap: 5, letterSpacing: ".1em", color: "rgba(232,238,244,.62)" }}>8 questions affected</span>
            <span className="velova-mono-label" style={{ display: "flex", alignItems: "center", gap: 5, letterSpacing: ".1em", color: "rgba(232,238,244,.62)" }}>Repeated pattern</span>
          </div>
        </div>
      </section>

      <style jsx>{`
        @keyframes vhvThink {
          0%, 100% { opacity: .2; transform: translateY(0); }
          50% { opacity: .9; transform: translateY(-2px); }
        }
        @keyframes vhvScan {
          0% { transform: translateY(0); opacity: 0; }
          14% { opacity: .5; }
          86% { opacity: .5; }
          100% { transform: translateY(210px); opacity: 0; }
        }
      `}</style>
    </div>
  )
}
